// pages/ShopApp.tsx - Complete JSON-Acceptable Version
import { useState, useEffect } from 'react';
import {
    FaShoppingBag,
    FaSearch,
    FaHeart,
    FaStar,
    FaShieldAlt,
    FaThLarge,
    FaThList,
    FaArrowUp,
    FaCheckCircle,
    FaBox,
} from 'react-icons/fa';
import { ScrollToTop } from '@/components/ScrollToTop';
import Cart, { CartItem } from './components/Cart';
import Checkout from './pages/Checkout/index';
import "./index.css";
import MyOrders from './pages/MyOrders';
import ShopHeader from './components/ShopHeader';
import { useAuth } from './hooks/useAuth';
import AuthModal from './components/AuthModal';
import { StarRating } from './components/StarRating';
import { QuickViewModal } from './components/QuickViewModel';
import Footer from './components/Footer';
import Testimonials from './components/Testimonials';
import { scrollToSection } from './lib/scrollToSection';

// ============================================================
// 1. DATA INTERFACES
// ============================================================
export interface Product {
    id: number;
    name: string;
    price: number;
    originalPrice?: number;
    description: string;
    category: string;
    image: string;
    images?: string[];
    rating: number;
    reviews: number;
    inStock: boolean;
    isNew?: boolean;
    isSale?: boolean;
    discount?: number;
    colors?: string[];
    sizes?: string[];
    features?: string[];
    shippingFee?: number;
    freeShipping?: boolean;
}

export interface Category {
    id: string;
    name: string;
    icon: string;
    count: number;
}

export interface Testimonial {
    id: number;
    name: string;
    avatar: string;
    rating: number;
    comment: string;
    date: string;
}

export interface ShopData {
    name: string;
    logo?: string;
    hero: {
        title: string;
        subtitle: string;
        backgroundImage: string;
        tag: string;
    };
    categories: Category[];
    products: Product[];
    featuredProducts: number[];
    testimonials: Testimonial[];
    stats: { label: string; value: string; icon: string }[];
    contact: {
        phone: string;
        email: string;
        address: string;
        social: { platform: string; url: string; icon: string; color: string }[];
    };
    footer: {
        title: string;
        description: string;
        copyright: string;
    };
}

// ============================================================
// 2. LOAD DATA FROM JSON
// ============================================================
// Import JSON data - this will work with any valid JSON file
import shopDataJson from './myshop.json';

// Default data as fallback
const defaultShopData: ShopData = shopDataJson as ShopData;

// ============================================================
// 3. PRODUCT CARD COMPONENT
// ============================================================
const ProductCard = ({ 
    product, 
    onQuickView, 
    addToCart 
}: { 
    product: Product; 
    onQuickView: (product: Product) => void; 
    addToCart: (product: Product) => void;
}) => {
    return (
        <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
            <div className="relative overflow-hidden">
                <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {product.isNew && (
                    <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        NEW
                    </span>
                )}
                {product.isSale && (
                    <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        -{product.discount}%
                    </span>
                )}
                <button
                    onClick={() => onQuickView(product)}
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm text-gray-800 px-4 py-2 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-105"
                >
                    Quick View
                </button>
            </div>
            <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 uppercase tracking-wider">{product.category}</span>
                    <button className="text-gray-400 hover:text-red-500 transition-colors duration-200">
                        <FaHeart />
                    </button>
                </div>
                <h3 className="font-primary font-bold text-lg text-gray-800 mb-2 line-clamp-1">
                    {product.name}
                </h3>
                <div className="flex items-center gap-2 mb-3">
                    <StarRating rating={product.rating} />
                    <span className="text-xs text-gray-500">({product.reviews})</span>
                </div>
                <div className="flex items-center gap-2 mb-4">
                    <span className="font-primary font-bold text-xl text-gray-800">${product.price}</span>
                    {product.originalPrice && (
                        <span className="text-sm text-gray-400 line-through">${product.originalPrice}</span>
                    )}
                </div>
                <button
                    onClick={() => addToCart(product)}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-primary font-semibold py-2.5 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                    <FaShoppingBag className="text-sm" />
                    Add to Cart
                </button>
            </div>
        </div>
    );
};

// ============================================================
// 4. MAIN COMPONENT
// ============================================================
interface ShopAppProps {
    data?: ShopData;
}

export default function MyShopFront({ data = defaultShopData }: ShopAppProps) {
    const { user, loading, isLoggedIn, login, logout } = useAuth();
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
    const [cartItems, setCartItems] = useState<CartItem[]>([]);
    const [orderComplete, setOrderComplete] = useState(false);
    const [showOrders, setShowOrders] = useState(false);

    // Add to cart
    const addToCart = (product: Product, quantity: number = 1) => {
        setCartItems(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                return prev.map(item =>
                    item.id === product.id
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }
            return [...prev, { ...product, quantity, image: product.image }];
        });
    };

    const updateQuantity = (id: number, quantity: number) => {
        setCartItems(prev =>
            prev.map(item =>
                item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
            )
        );
    };

    const removeItem = (id: number) => {
        setCartItems(prev => prev.filter(item => item.id !== id));
    };

    const handleCheckout = () => {
        setIsCartOpen(false);
        setIsCheckoutOpen(true);
    };

    const handleOrderComplete = (orderData: any) => {
        console.log('Order completed:', orderData);
        setCartItems([]);
        setIsCheckoutOpen(false);
        setOrderComplete(true);
        setTimeout(() => setOrderComplete(false), 5000);
    };

    const { hero, categories, products, featuredProducts, testimonials, stats, contact, footer } = data;

    // Filter products
    const filteredProducts = products.filter(product => {
        const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
        const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.description.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const featuredItems = products.filter(p => featuredProducts.includes(p.id));

    const handleQuickView = (product: Product) => {
        setSelectedProduct(product);
        setIsQuickViewOpen(true);
    };

    // Scroll to top
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 500);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSearchClick = () => {
        const element = document.getElementById('products');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleWishlistClick = () => {
        alert('Wishlist feature coming soon!');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <ScrollToTop />

            {/* HERO SECTION */}
            <section
                id="home"
                className="relative min-h-[600px] flex items-center justify-center bg-cover bg-center"
                style={{ backgroundImage: `url(${hero.backgroundImage})` }}
            >
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />

                <ShopHeader
                    siteName={data.name}
                    logo={<FaShoppingBag className="text-3xl" />}
                    links={[
                        { label: 'Home', target: 'home' },
                        { label: 'Products', target: 'products' },
                        { label: 'Reviews', target: 'testimonials' },
                        { label: 'Contact', target: 'contact' },
                    ]}
                    homeSection="home"
                    cartItemCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                    onCartClick={() => setIsCartOpen(true)}
                    onOrdersClick={() => setShowOrders(true)}
                    onSearchClick={handleSearchClick}
                    onWishlistClick={handleWishlistClick}
                    isLoggedIn={isLoggedIn}
                    user={user}
                    onLoginClick={() => setIsAuthModalOpen(true)}
                    onLogoutClick={logout}
                />

                <div className="relative z-10 text-center px-4 max-w-4xl">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <span className="px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium tracking-wider">
                            {hero.tag}
                        </span>
                    </div>
                    <h1 className="text-5xl md:text-6xl lg:text-7xl font-primary font-bold text-white mb-6">
                        {hero.title}
                    </h1>
                    <p className="text-xl md:text-2xl text-white/90 font-secondary leading-relaxed mb-8">
                        {hero.subtitle}
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                        <button
                            onClick={() => scrollToSection('products')}
                            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full font-primary font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                            Shop Now
                        </button>
                        <button className="px-8 py-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-full font-primary font-semibold transition-all duration-300 border border-white/30">
                            Explore Collections
                        </button>
                    </div>
                </div>
            </section>

            {/* STATS SECTION */}
            <section className="container mx-auto max-w-7xl px-4 -mt-16 relative z-20">
                <div className="bg-white rounded-3xl shadow-2xl p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {stats.map((stat, index) => {
                        const iconMap: Record<string, React.ReactNode> = {
                            FaHeart: <FaHeart />,
                            FaShoppingBag: <FaShoppingBag />,
                            FaStar: <FaStar />,
                            FaShieldAlt: <FaShieldAlt />,
                        };
                        return (
                            <div key={index} className="text-center">
                                <div className="text-3xl text-blue-600 mb-2 flex justify-center">
                                    {iconMap[stat.icon]}
                                </div>
                                <p className="font-primary text-2xl font-bold text-gray-800">{stat.value}</p>
                                <p className="font-secondary text-sm text-gray-500">{stat.label}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="container mx-auto max-w-7xl px-4 py-20">
                <div className="text-center mb-12">
                    <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
                        Featured
                    </span>
                    <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                        Best Sellers
                    </h2>
                    <p className="font-secondary text-gray-500 max-w-2xl mx-auto">
                        Discover our most popular products loved by thousands of customers
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {featuredItems.map((product) => (
                        <ProductCard key={product.id} product={product} onQuickView={handleQuickView} addToCart={addToCart} />
                    ))}
                </div>
            </section>

            {/* PRODUCTS SECTION */}
            <section id="products" className="bg-gradient-to-b from-blue-50 to-white py-20">
                <div className="container mx-auto max-w-7xl px-4">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
                        <div>
                            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-2">
                                Shop
                            </span>
                            <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800">
                                All Products
                            </h2>
                        </div>
                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <div className="relative flex-1 md:w-64">
                                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search products..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all duration-200"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-2 rounded-lg transition-colors duration-200 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}
                                >
                                    <FaThLarge />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-2 rounded-lg transition-colors duration-200 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}
                                >
                                    <FaThList />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-8">
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${selectedCategory === category.id
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'bg-white text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>

                    <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'} gap-6`}>
                        {filteredProducts.map((product) => (
                            <ProductCard key={product.id} product={product} onQuickView={handleQuickView} addToCart={addToCart} />
                        ))}
                    </div>

                    {filteredProducts.length === 0 && (
                        <div className="text-center py-16">
                            <div className="text-6xl mb-4">🔍</div>
                            <h3 className="font-primary text-2xl font-bold text-gray-800 mb-2">No products found</h3>
                            <p className="font-secondary text-gray-500">Try adjusting your search or filter</p>
                        </div>
                    )}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <Testimonials testimonials={testimonials} />

            {/* FOOTER */}
            <Footer
                footer={footer}
                contact={contact}
                logo={<FaShoppingBag className="text-3xl" />}
                links={[
                    { label: "Home", target: "home" },
                    { label: "Products", target: "products" },
                    { label: "Reviews", target: "testimonials" },
                    { label: "Contact", target: "contact" },
                ]}
                onNavigate={(target) => {
                    document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
                }}
            />

            {/* MODALS */}
            {showOrders && (
                <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
                    <MyOrders
                        onBack={() => setShowOrders(false)}
                        isLoggedIn={isLoggedIn}
                        setIsAuthModalOpen={setIsAuthModalOpen}
                    />
                </div>
            )}

            <AuthModal
                isOpen={isAuthModalOpen}
                onClose={() => setIsAuthModalOpen(false)}
                onLoginSuccess={(userData) => {
                    login(userData);
                }}
            />

            <Cart
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
                items={cartItems}
                onUpdateQuantity={updateQuantity}
                onRemoveItem={removeItem}
                onCheckout={handleCheckout}
            />

            <Checkout
                isOpen={isCheckoutOpen}
                onClose={() => setIsCheckoutOpen(false)}
                items={cartItems}
                total={cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)}
                onComplete={handleOrderComplete}
            />

            {orderComplete && (
                <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-slide-down">
                    <FaCheckCircle className="text-xl" />
                    <span className="font-primary font-semibold">Order placed successfully! 🎉</span>
                </div>
            )}

            {isQuickViewOpen && (
                <QuickViewModal product={selectedProduct} onClose={() => setIsQuickViewOpen(false)} addToCart={addToCart} />
            )}

            {showScrollTop && (
                <button
                    onClick={scrollToTop}
                    className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white w-12 h-12 rounded-full shadow-lg transition-all duration-200 hover:scale-110 flex items-center justify-center"
                >
                    <FaArrowUp className="text-xl" />
                </button>
            )}
        </div>
    );
}

// ============================================================
// 5. EXPORTS
// ============================================================
export { defaultShopData, type ShopData, type Product, type Category };